import React from "react";
import { Box } from "@mui/material";
import { keyframes } from "@mui/system";

import { subscribeToAudioLevel } from "utils";

/**
 * Speaking indicator (mobile) — mobile port of desktop `components/MicTestAnimation`
 * (shown beside the question header while RECORDING in `Assessment/QuestionSection`).
 *
 * A blue pill with three white bars that bounce with the candidate's voice, so
 * they can see the mic is live and their answer is being picked up. Driven by the
 * shared `subscribeToAudioLevel` meter, exactly like desktop — the bar-set and
 * level thresholds (>100 / >50 / >10 / silent) match `MicTestAnimation`.
 *
 * Like desktop it opens its own short-lived audio stream while active; render it
 * only during RECORDING so the stream is released as soon as recording stops.
 */

// Bounce keyframes — same scaleY steps as desktop's low/medium/high/veryHigh.
const low = keyframes`
  25% { transform: scaleY(0.3); }
  50% { transform: scaleY(0.2); }
  75% { transform: scaleY(0.4); }
`;
const medium = keyframes`
  25% { transform: scaleY(0.5); }
  50% { transform: scaleY(0.3); }
  75% { transform: scaleY(0.5); }
`;
const high = keyframes`
  25% { transform: scaleY(0.8); }
  50% { transform: scaleY(0.4); }
  75% { transform: scaleY(0.8); }
`;
const veryHigh = keyframes`
  25% { transform: scaleY(1); }
  50% { transform: scaleY(0.4); }
  75% { transform: scaleY(0.6); }
`;

const ANIM = { low, medium, high, veryHigh };

// Resting (silent) bar — a small static mark, as desktop's `stop`.
const REST_SX = {
  width: 3,
  height: "65%",
  borderRadius: "50%",
  bgcolor: "shades.clr-white-900",
  transform: "scaleY(0.2)",
};

function barSx(anim) {
  if (!anim) return REST_SX;
  return {
    width: 4,
    height: "80%",
    borderRadius: "4px",
    bgcolor: "shades.clr-white-900",
    transform: "scaleY(0.2)",
    animation: `${ANIM[anim]} 1.2s ease-in-out infinite`,
  };
}

export default function SpeakingIndicator({ start = true }) {
  const [level, setLevel] = React.useState(0);
  const unsubscribeRef = React.useRef(null);

  React.useEffect(() => {
    if (!start) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        unsubscribeRef.current = subscribeToAudioLevel(stream, setLevel);
      } catch (error) {
        console.error(error);
      }
    })();

    return () => {
      cancelled = true;
      if (typeof unsubscribeRef.current === "function") {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [start]);

  // Bounce intensity by loudness — same thresholds as desktop MicTestAnimation.
  let side = null;
  let middle = null;
  if (level > 100) {
    side = "high";
    middle = "veryHigh";
  } else if (level > 50) {
    side = "medium";
    middle = "high";
  } else if (level > 10) {
    side = "low";
    middle = "medium";
  }

  return (
    <Box
      aria-label="Recording — microphone active"
      sx={{
        flexShrink: 0,
        width: 28,
        height: 28,
        borderRadius: "50%",
        bgcolor: "primary.main",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "4px",
      }}
    >
      <Box sx={barSx(side)} />
      <Box sx={barSx(middle)} />
      <Box sx={barSx(side)} />
    </Box>
  );
}
